export type LoanStage = 'credit_pull' | 'doc_out' | 'submission' | 'closing'

export interface StageEvent {
  stage: LoanStage
  date: string // ISO yyyy-mm-dd
  points: number
}

export interface Loan {
  id: string
  borrower: string
  property: string
  loanAmount: number
  loanType: 'Conventional' | 'FHA' | 'VA' | 'Jumbo'
  currentStage: LoanStage
  events: StageEvent[]
  isMoving: boolean
  daysInCurrentStage: number
  speedBonus?: number
}

export const STAGE_LABELS: Record<LoanStage, string> = {
  credit_pull: 'Credit Pull',
  doc_out: 'Doc Out',
  submission: 'Submission',
  closing: 'Closing',
}

export const STAGE_POINTS: Record<LoanStage, number> = {
  credit_pull: 750,
  doc_out: 500,
  submission: 500,
  closing: 250,
}

export const STAGE_COLORS: Record<
  LoanStage,
  { text: string; bg: string; border: string }
> = {
  credit_pull: {
    text: 'text-blue-700',
    bg: 'bg-blue-100',
    border: 'border-blue-200',
  },
  doc_out: {
    text: 'text-green-700',
    bg: 'bg-green-100',
    border: 'border-green-200',
  },
  submission: {
    text: 'text-purple-700',
    bg: 'bg-purple-100',
    border: 'border-purple-200',
  },
  closing: {
    text: 'text-orange-600',
    bg: 'bg-orange-100',
    border: 'border-orange-200',
  },
}

// May 14, 2025 is "today"
// MTD funnel: 8 pulls, 6 doc outs, 4 submissions, 3 closings
// Today: 1 pull, 1 doc out, 1 submission, 0 closings
export const LOANS: Loan[] = [
  // Closed loans (3) - full lifecycle
  {
    id: 'LN-2025-001',
    borrower: 'Robert & Linda Martinez',
    property: '1245 Maple Ave, Austin, TX 78704',
    loanAmount: 425000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 1,
    speedBonus: 1000,
    events: [
      { stage: 'credit_pull', date: '2025-05-01', points: 750 },
      { stage: 'doc_out', date: '2025-05-03', points: 500 },
      { stage: 'submission', date: '2025-05-08', points: 500 },
      { stage: 'closing', date: '2025-05-13', points: 250 },
    ],
  },
  {
    id: 'LN-2025-002',
    borrower: 'Jennifer Patel',
    property: '892 Oakwood Dr, Round Rock, TX 78664',
    loanAmount: 312000,
    loanType: 'FHA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 4,
    events: [
      { stage: 'credit_pull', date: '2025-04-22', points: 750 },
      { stage: 'doc_out', date: '2025-04-28', points: 500 },
      { stage: 'submission', date: '2025-05-05', points: 500 },
      { stage: 'closing', date: '2025-05-10', points: 250 },
    ],
  },
  {
    id: 'LN-2025-003',
    borrower: 'Marcus Thompson',
    property: '4501 Cedar Ln, Cedar Park, TX 78613',
    loanAmount: 580000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 7,
    events: [
      { stage: 'credit_pull', date: '2025-04-15', points: 750 },
      { stage: 'doc_out', date: '2025-04-20', points: 500 },
      { stage: 'submission', date: '2025-04-30', points: 500 },
      { stage: 'closing', date: '2025-05-07', points: 250 },
    ],
  },
  // Loans in Submission (1 more, on top of the 3 that already submitted, = 4 total submissions MTD)
  {
    id: 'LN-2025-004',
    borrower: 'Sophia Nguyen',
    property: '7820 Riverbend Rd, Austin, TX 78746',
    loanAmount: 695000,
    loanType: 'Jumbo',
    currentStage: 'submission',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-02', points: 750 },
      { stage: 'doc_out', date: '2025-05-06', points: 500 },
      { stage: 'submission', date: '2025-05-14', points: 500 },
    ],
  },
  // Loans in Doc Out (2 more, on top of the 4 that already moved past = 6 doc outs MTD)
  {
    id: 'LN-2025-005',
    borrower: 'David & Amy Chen',
    property: '331 Hillcrest Way, Pflugerville, TX 78660',
    loanAmount: 385000,
    loanType: 'Conventional',
    currentStage: 'doc_out',
    isMoving: false,
    daysInCurrentStage: 6,
    events: [
      { stage: 'credit_pull', date: '2025-05-04', points: 750 },
      { stage: 'doc_out', date: '2025-05-08', points: 500 },
    ],
  },
  {
    id: 'LN-2025-006',
    borrower: 'Carlos Reyes',
    property: '156 Sunset Blvd, Leander, TX 78641',
    loanAmount: 298000,
    loanType: 'VA',
    currentStage: 'doc_out',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-09', points: 750 },
      { stage: 'doc_out', date: '2025-05-14', points: 500 },
    ],
  },
  // Loans in Credit Pull (2 more, on top of the 6 that already moved past = 8 pulls MTD)
  {
    id: 'LN-2025-007',
    borrower: 'Olivia Brooks',
    property: '2244 Greenwood St, Austin, TX 78702',
    loanAmount: 445000,
    loanType: 'Conventional',
    currentStage: 'credit_pull',
    isMoving: true,
    daysInCurrentStage: 2,
    events: [{ stage: 'credit_pull', date: '2025-05-12', points: 750 }],
  },
  {
    id: 'LN-2025-008',
    borrower: 'James Whitfield',
    property: '6708 Lakeshore Dr, Austin, TX 78732',
    loanAmount: 825000,
    loanType: 'Jumbo',
    currentStage: 'credit_pull',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [{ stage: 'credit_pull', date: '2025-05-14', points: 750 }],
  },
]

// Filter helpers
export type Scope = 'today' | 'mtd' | 'all'

export const TODAY = '2025-05-14'
export const MONTH_START = '2025-05-01'

export function filterLoansByStageAndScope(
  loans: Loan[],
  stage: LoanStage,
  scope: Scope,
): Loan[] {
  return loans.filter((loan) => {
    const event = loan.events.find((e) => e.stage === stage)
    if (!event) return false
    if (scope === 'today') return event.date === TODAY
    if (scope === 'mtd') return event.date >= MONTH_START && event.date <= TODAY
    return true
  })
}

export function getEventDate(loan: Loan, stage: LoanStage): string | undefined {
  return loan.events.find((e) => e.stage === stage)?.date
}

export function getTotalPoints(loans: Loan[], stage: LoanStage): number {
  return loans.reduce((sum, loan) => {
    const e = loan.events.find((ev) => ev.stage === stage)
    return e ? sum + e.points : sum
  }, 0)
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
